import React from 'react';
import { connect, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { IThunkDispatch } from 'store/middleware';

import { memoizedGroupBySelector } from '@travelnest/entities';

import { api } from './data/api';
import { IExperiments } from './data/entities/experiments';
import { actions as entityActions } from './data/store';
import { currentUserId } from './entities/auth/session/selectors';
import { logging } from './logging';
import { IState } from './store/reducer';
import { Loader } from './view/components/loader/Loader';
import { withProductFrame } from './view/components/product-frame/ProductFrame';
import { useDataLoad } from './view/helpers/hooks';

declare global {
	// tslint:disable-next-line: interface-name
	interface Window {
		// Use "travelnest" to not pollute any namespaces
		travelnest: {
			experiments: any;
		};
	}
}

/**
 * Component to expose the experiments to the DevTools console for easy debugging without needing the Redux Dev Tools
 */
const ExperimentExposer = () => {
	const customers = useSelector((state: IState) => state.data.experiments?.byCustomerId) || {};
	const properties = useSelector((state: IState) => state.data.experiments?.byPropertyId) || {};

	window.travelnest = { experiments: { customers, properties } };
	return null;
};

interface IProps {
	customerId: string;
	getCustomerExperiments: (customerId: string) => any;
	getCustomerProperties: (customerId: string) => any;
	getPropertyExperiments: (propertyId: number) => any;
	children: JSX.Element;
}

const EXPERIMENT_TIMEOUT = 1000;

/**
 * Wait for the given amount of time, and fail.
 * This is useful for forcing timeouts with other potentially long-running async tasks.
 * @param ms Number of milliseconds to wait for
 * @returns A Promise that will fail after the given number of ms
 */
const wait = (ms: number) => {
	return new Promise((_, reject) => {
		setTimeout(() => reject(new Error('Data fetching timed out')), ms);
	});
};

const Loading: React.FC = () => {
	return <Loader loading />;
};

/**
 * Provider to block any further loading until we load all customer and property experiments into Redux
 * This will ensure that the experiment information can be accessed in Redux by the use...Experiment hooks at any point
 * Will timeout and still let things load if the loading takes too long
 * @returns Loading spinner whilst loading, or children when loaded/timed out
 */
export const ExperimentProviderComponent = (
	{ customerId, getCustomerExperiments, getCustomerProperties, getPropertyExperiments, children }: IProps) => {
	const store = useStore<IState>();

	// The `tbp` route is only meant to be used as an iFrame within portal-next,
	// so we don't need to use the product frame when loading
	const route = useLocation();
	const useProductFrame = !route.pathname.startsWith('/tbp/');
	const LoadingScreen = useProductFrame ? withProductFrame(Loading) : Loading;

	const isLoading = useDataLoad(!!customerId, async () => {
		const tasks = [];

		tasks.push(getCustomerExperiments(customerId));

		// Need to wait to get all of the properties that an owner has,
		// so we can loop through all of their properties and get the property experiments
		await getCustomerProperties(customerId);
		const properties = memoizedGroupBySelector('owner_id', store.getState().data.properties.byId)[customerId] || [];
		for (const property of properties) {
			tasks.push(getPropertyExperiments(property.id));
		}

		try {
			await Promise.race([wait(EXPERIMENT_TIMEOUT), Promise.all(tasks)]);
		} catch (e) {
			logging.error(`Failed to load experiments: ${e}`);
		}
	});

	return isLoading ? <LoadingScreen/> : <><ExperimentExposer />{children}</>;
};

/**
 * Get all of the experiments we have stored in Redux for the given property ID
 * This should have been populated at initialisation, but we may have failed to retrieve this data,
 * so the caller should handle the case where the experiment is not available (fall back to the default)
 * @param propertyId
 * @returns {"experiment_name1": "experiment_result1", "experiment_name2": "experiment_result2"..}, or {} if we failed to get experiment data
 */
export const usePropertyExperiments = (propertyId: number): IExperiments => {
	return useSelector((state: IState) => state.data.experiments?.byPropertyId[propertyId]) || {};
};

/**
 * Get all of the experiments we have stored in Redux for the given customer ID
 * This should have been populated at initialisation, but we may have failed to retrieve this data,
 * so the caller should handle the case where the experiment is not available (fall back to the default)
 * @param customerId
 * @returns {"experiment_name1": "experiment_result1", "experiment_name2": "experiment_result2"..}, or {} if we failed to get experiment data
 */
export const useCustomerExperiments = (customerId: string): IExperiments => {
	return useSelector((state: IState) => state.data.experiments?.byCustomerId[customerId]) || {};
};

/**
 * Send an experiment event for the given property ID
 * This does not wait for sending to complete as this is a simple fire-and-forget endpoint
 * @param propertyId
 * @param eventType
 * @param eventValue
 */
export const sendPropertyEvent = (propertyId: number, eventType: string, eventValue: number) => {
	const event = { 'event_type': eventType, 'event_value': eventValue };
	api.experiments.sendPropertyEvent(propertyId, event);
};

/**
 * Send an experiment event for the given customer ID
 * This does not wait for sending to complete as this is a simple fire-and-forget endpoint
 * @param customerId
 * @param eventType
 * @param eventValue
 */
export const sendCustomerEvent = (customerId: string, eventType: string, eventValue: number) => {
	const event = { 'event_type': eventType, 'event_value': eventValue };
	api.experiments.sendCustomerEvent(customerId, event);
};

const mapStateToProps = (state: IState) => ({
	// Map the customer ID so that if the customer ID changes (e.g. for impersonation) the experiments are refetched
	customerId: currentUserId(state)
});

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	getCustomerExperiments: (customerId: string) => dispatch(entityActions.experiments.getByCustomerId(customerId)),
	getCustomerProperties: (customerId: string) => dispatch(entityActions.property.getByOwnerId(customerId)),
	getPropertyExperiments: (propertyId: number) => dispatch(entityActions.experiments.getByPropertyId(propertyId)),
});

export const ExperimentProvider = connect(mapStateToProps, mapDispatchToProps)(ExperimentProviderComponent);
